import React from 'react';

/**
 * Interface for notification item used in the UI
 */
export interface NotificationItem {
    id: string | number;
    type: string;
    icon: React.ReactNode;
    bgColor?: string; // Optional - no longer used
    title: string;
    time: string;
    hasThumbnail: boolean;
    thumbnailUrl?: string;
    hasAction: boolean;
    actionText?: string;
    actionRoute?: string;
}

/**
 * Create user signup icon component
 */
const createUserSignupIcon = (): React.ReactNode => {
    return (
        <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.799805" y="0.415283" width="44" height="44" rx="22" fill="#F2FCFA" />
            <path d="M26.1335 29.9153V28.2486C26.1335 27.3646 25.7823 26.5167 25.1572 25.8916C24.532 25.2665 23.6842 24.9153 22.8001 24.9153H17.8001C16.9161 24.9153 16.0682 25.2665 15.4431 25.8916C14.818 26.5167 14.4668 27.3646 14.4668 28.2486V29.9153" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20.3001 21.5819C22.1411 21.5819 23.6335 20.0896 23.6335 18.2486C23.6335 16.4077 22.1411 14.9153 20.3001 14.9153C18.4592 14.9153 16.9668 16.4077 16.9668 18.2486C16.9668 20.0896 18.4592 21.5819 20.3001 21.5819Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M28.6328 19.0819V24.0819" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M31.1328 21.5819H26.1328" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

/**
 * Create survey completion icon component
 */
const createSurveyCompletionIcon = (): React.ReactNode => {
    return (
        <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.799805" y="0.0152588" width="44" height="44" rx="22" fill="#F2FCFA" />
            <g clipPath="url(#clip0_17052_13902)">
                <path d="M25.3001 13.6819H20.3001C19.8399 13.6819 19.4668 14.055 19.4668 14.5152V16.1819C19.4668 16.6421 19.8399 17.0152 20.3001 17.0152H25.3001C25.7604 17.0152 26.1335 16.6421 26.1335 16.1819V14.5152C26.1335 14.055 25.7604 13.6819 25.3001 13.6819Z" stroke="#00C49A" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M26.1328 15.3486H27.7995C28.2415 15.3486 28.6654 15.5242 28.978 15.8368C29.2906 16.1493 29.4661 16.5733 29.4661 17.0153V28.682C29.4661 29.124 29.2906 29.5479 28.978 29.8605C28.6654 30.173 28.2415 30.3486 27.7995 30.3486H17.7995C17.3575 30.3486 16.9335 30.173 16.621 29.8605C16.3084 29.5479 16.1328 29.124 16.1328 28.682V17.0153C16.1328 16.5733 16.3084 16.1493 16.621 15.8368C16.9335 15.5242 17.3575 15.3486 17.7995 15.3486H19.4661" stroke="#00C49A" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22.7998 21.1819H26.1331" stroke="#00C49A" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22.7998 25.3486H26.1331" stroke="#00C49A" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19.4668 21.1819H19.4751" stroke="#00C49A" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19.4668 25.3486H19.4751" stroke="#00C49A" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <defs>
                <clipPath id="clip0_17052_13902">
                    <rect width="20" height="20" fill="white" transform="translate(12.7998 12.0153)" />
                </clipPath>
            </defs>
        </svg>
    );
};

/**
 * Create event registration icon component
 */
const createEventRegistrationIcon = (): React.ReactNode => {
    return (
        <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.799805" y="0.0152588" width="44" height="44" rx="22" fill="#F2FCFA" />
            <path d="M19.4668 13.6819V17.0152" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M26.1328 13.6819V17.0152" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M28.6331 15.3486H16.9665C16.046 15.3486 15.2998 16.0948 15.2998 17.0153V28.682C15.2998 29.6024 16.046 30.3486 16.9665 30.3486H28.6331C29.5536 30.3486 30.2998 29.6024 30.2998 28.682V17.0153C30.2998 16.0948 29.5536 15.3486 28.6331 15.3486Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15.2998 20.3486H30.2998" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

/**
 * Create support ticket icon component
 */
const createSupportTicketIcon = (): React.ReactNode => {
    return (
        <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.799805" y="0.315308" width="44" height="44" rx="22" fill="#F2FCFA" />
            <path d="M15.2932 25.9336C15.4157 26.2427 15.443 26.5814 15.3715 26.9061L14.484 29.6478C14.4554 29.7868 14.4628 29.9308 14.5055 30.0662C14.5482 30.2016 14.6248 30.3238 14.7279 30.4213C14.8311 30.5188 14.9575 30.5883 15.095 30.6233C15.2326 30.6583 15.3768 30.6575 15.514 30.6211L18.3582 29.7894C18.6646 29.7286 18.982 29.7552 19.274 29.8661C21.0535 30.6971 23.0693 30.8729 24.9658 30.3625C26.8623 29.8521 28.5176 28.6883 29.6396 27.0764C30.7616 25.4645 31.2783 23.5081 31.0985 21.5524C30.9186 19.5967 30.0538 17.7673 28.6566 16.3871C27.2594 15.0069 25.4196 14.1645 23.4619 14.0085C21.5041 13.8526 19.5542 14.3931 17.9561 15.5347C16.358 16.6763 15.2145 18.3457 14.7273 20.2483C14.2401 22.1509 14.4405 24.1644 15.2932 25.9336Z" stroke="#00C49A" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

/**
 * Create default icon component
 */
const createDefaultIcon = (): React.ReactNode => {
    return (
        <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.799805" y="0.0152588" width="44" height="44" rx="22" fill="#F2FCFA" />
            <path d="M19.4668 13.6819V17.0152" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M26.1328 13.6819V17.0152" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M28.6331 15.3486H16.9665C16.046 15.3486 15.2998 16.0948 15.2998 17.0153V28.682C15.2998 29.6024 16.046 30.3486 16.9665 30.3486H28.6331C29.5536 30.3486 30.2998 29.6024 30.2998 28.682V17.0153C30.2998 16.0948 29.5536 15.3486 28.6331 15.3486Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15.2998 20.3486H30.2998" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

const createUserRegisterIcon = () => {
    return (
      <svg
        width='44'
        height='44'
        viewBox='0 0 44 44'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <rect
          width='44'
          height='44'
          rx='22'
          fill='#F2FCFA'
        />
        <path
          d='M25.3327 29.5V27.8333C25.3327 26.9493 24.9815 26.1014 24.3564 25.4763C23.7313 24.8512 22.8834 24.5 21.9993 24.5H16.9993C16.1153 24.5 15.2674 24.8512 14.6423 25.4763C14.0172 26.1014 13.666 26.9493 13.666 27.8333V29.5'
          stroke='#00C49A'
          stroke-width='1.66667'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
        <path
          d='M19.4993 21.1667C21.3403 21.1667 22.8327 19.6743 22.8327 17.8333C22.8327 15.9924 21.3403 14.5 19.4993 14.5C17.6584 14.5 16.166 15.9924 16.166 17.8333C16.166 19.6743 17.6584 21.1667 19.4993 21.1667Z'
          stroke='#00C49A'
          stroke-width='1.66667'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
        <path
          d='M27.834 18.6667V23.6667'
          stroke='#00C49A'
          stroke-width='1.66667'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
        <path
          d='M30.334 21.1667H25.334'
          stroke='#00C49A'
          stroke-width='1.66667'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
      </svg>
    );
}

const createUserRegisterEventIcon = () => {
    return (
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="44" height="44" rx="22" fill="#F2FCFA"/>
            <path d="M18.666 13.6667V17.0001" stroke="#00C49A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M25.334 13.6667V17.0001" stroke="#00C49A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M27.8333 15.3333H16.1667C15.2462 15.3333 14.5 16.0794 14.5 16.9999V28.6666C14.5 29.5871 15.2462 30.3333 16.1667 30.3333H27.8333C28.7538 30.3333 29.5 29.5871 29.5 28.6666V16.9999C29.5 16.0794 28.7538 15.3333 27.8333 15.3333Z" stroke="#00C49A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M14.5 20.3333H29.5" stroke="#00C49A" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    );
}

const createUserCompleteEventIcon = () => {
    return (
      <svg
        width='44'
        height='44'
        viewBox='0 0 44 44'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <rect
          width='44'
          height='44'
          rx='22'
          fill='#F2FCFA'
        />
        <path
          d='M24.4993 13.6667H19.4993C19.0391 13.6667 18.666 14.0398 18.666 14.5001V16.1667C18.666 16.627 19.0391 17.0001 19.4993 17.0001H24.4993C24.9596 17.0001 25.3327 16.627 25.3327 16.1667V14.5001C25.3327 14.0398 24.9596 13.6667 24.4993 13.6667Z'
          stroke='#00C49A'
          stroke-width='1.67'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
        <path
          d='M25.334 15.3333H27.0007C27.4427 15.3333 27.8666 15.5088 28.1792 15.8214C28.4917 16.134 28.6673 16.5579 28.6673 16.9999V28.6666C28.6673 29.1086 28.4917 29.5325 28.1792 29.8451C27.8666 30.1577 27.4427 30.3333 27.0007 30.3333H17.0007C16.5586 30.3333 16.1347 30.1577 15.8221 29.8451C15.5096 29.5325 15.334 29.1086 15.334 28.6666V16.9999C15.334 16.5579 15.5096 16.134 15.8221 15.8214C16.1347 15.5088 16.5586 15.3333 17.0007 15.3333H18.6673'
          stroke='#00C49A'
          stroke-width='1.67'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
        <path
          d='M22 21.1667H25.3333'
          stroke='#00C49A'
          stroke-width='1.67'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
        <path
          d='M22 25.3333H25.3333'
          stroke='#00C49A'
          stroke-width='1.67'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
        <path
          d='M18.666 21.1667H18.6743'
          stroke='#00C49A'
          stroke-width='1.67'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
        <path
          d='M18.666 25.3333H18.6743'
          stroke='#00C49A'
          stroke-width='1.67'
          stroke-linecap='round'
          stroke-linejoin='round'
        />
      </svg>
    );
}

/**
 * Get the appropriate icon based on notification type
 * This uses the same logic as the Recent Activity section
 */
export const getNotificationIcon = (type: string): React.ReactNode => {
    const iconMap: Record<string, React.ReactNode> = {
        user_signup: createUserSignupIcon(),
        survey_completion: createSurveyCompletionIcon(),
        event_registration: createEventRegistrationIcon(),
        support_ticket: createSupportTicketIcon(),
        user_register: createUserRegisterIcon(),
        user_register_event: createUserRegisterEventIcon(),
        user_complete_event: createUserCompleteEventIcon(),
        // TODO: Add valid icon for redeem and submit new survey
        user_redeem_coupon: createDefaultIcon(),
        user_submit_new_survey: createDefaultIcon()
    };

    return iconMap[type] || createDefaultIcon();
};

/**
 * Format timestamp to relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (timestamp: string): string => {
    try {
        const now = new Date();
        const past = new Date(timestamp);
        const diffInMilliseconds = now.getTime() - past.getTime();
        const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

        const diffInWeeks = Math.floor(diffInDays / 7);
        if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;

        const diffInMonths = Math.floor(diffInDays / 30);
        return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    } catch (error) {
        console.error('Error formatting time:', error);
        return timestamp;
    }
};

/**
 * Determine notification type from activity title
 */
const getNotificationType = (title: string): string => {
    const lowerTitle = title.toLowerCase();

    // Check for user registration activities
    if (lowerTitle.includes('account') ||
        lowerTitle.includes('new user') ||
        (lowerTitle.includes('joined') && !lowerTitle.includes('event')) ||
        lowerTitle.includes('welcome') ||
        lowerTitle.includes('sign up') ||
        lowerTitle.includes('signup')) {
        return 'user_signup';
    }

    // Check for survey activities
    if (lowerTitle.includes('survey') ||
        lowerTitle.includes('completed') ||
        lowerTitle.includes('submitted') ||
        lowerTitle.includes('response')) {
        return 'survey_completion';
    }

    // Check for event activities
    if ((lowerTitle.includes('joined') && lowerTitle.includes('event')) ||
        lowerTitle.includes('earn points') ||
        lowerTitle.includes('event registration') ||
        lowerTitle.includes('registered to event') ||
        lowerTitle.includes('registered for')) {
        return 'event_registration';
    }

    // Check for support/ticket activities
    if (lowerTitle.includes('support') ||
        lowerTitle.includes('ticket') ||
        lowerTitle.includes('help') ||
        lowerTitle.includes('inquiry')) {
        return 'support_ticket';
    }

    // Default fallback
    return 'user_signup';
};

/**
 * Map recent activity data to notification item
 */
export const mapActivityToNotification = (activity: any): NotificationItem => {
    const type = getNotificationType(activity.title);
    const icon = getNotificationIcon(type);
    const hasThumbnail = type === 'event_registration';
    const hasAction = type === 'support_ticket';

    return {
        id: activity.id,
        type,
        icon,
        title: activity.title,
        time: formatRelativeTime(activity.created_at),
        hasThumbnail,
        thumbnailUrl: activity.metadata?.thumbnail,
        hasAction,
        actionText: hasAction ? 'View Ticket' : undefined,
        actionRoute: hasAction ? `/support/tickets/${activity.metadata?.ticketId || activity.id}` : undefined,
    };
};